import { Injectable, computed, inject, signal } from '@angular/core';
import { cancelableResource, QueryBuilder, QueryFilter } from '@softline/core';
import {
  ABC_ANALYSIS_PRODUCT_DETAIL_TOTAL_SALES_ORG,
  buildProductDetailWithTotal,
} from '../functions/abc-analysis-product-detail-total.function';
import { AbcAnalysisProductDetailRow } from '../types/abc-analysis-product-detail-row';
import { AbcAnalysisFilterStateService } from './abc-analysis-filter-state.service';
import { AbcAnalysisProductDetailGridService } from './abc-analysis-product-detail-grid.service';

@Injectable()
export class AbcAnalysisProductDetailService {
  private readonly productDetailGridService = inject(AbcAnalysisProductDetailGridService);
  private readonly filterState = inject(AbcAnalysisFilterStateService);

  readonly productNumber = signal<string | null>(null);
  readonly selectedSalesOrg = signal<string>(ABC_ANALYSIS_PRODUCT_DETAIL_TOTAL_SALES_ORG);

  private readonly detailResource = cancelableResource({
    params: () => ({
      productNumber: this.productNumber(),
      dateRangeFilter: this.filterState.dateRangeFilter(),
      master: this.filterState.master(),
    }),
    loader: async ({ params }) => {
      if (!params.productNumber) return [];
      // dateRangeFilter is typed against the overview row shape but its field
      // (period) isn't part of either endpoint's output columns.
      const q = new QueryBuilder<AbcAnalysisProductDetailRow>()
        .start(0)
        .end(100)
        .filter(params.dateRangeFilter as unknown as QueryFilter<AbcAnalysisProductDetailRow>);
      const result = await this.productDetailGridService.load({
        query: q.build(),
        master: { ...params.master, productNumber: params.productNumber },
      });
      // getLoadPayload always sends columns: [] (all columns), so the response is always
      // the full row despite QueryResult typing data as Partial<T>.
      return buildProductDetailWithTotal(result.data as AbcAnalysisProductDetailRow[]);
    },
  });

  readonly rows = computed(() => this.detailResource.value() ?? []);
  readonly isLoading = this.detailResource.isLoading;

  readonly selectedRow = computed(
    () => this.rows().find((row) => row.sales_organisation === this.selectedSalesOrg()) ?? this.rows()[0] ?? null
  );

  setProduct(productNumber: string): void {
    this.productNumber.set(productNumber);
    this.selectedSalesOrg.set(ABC_ANALYSIS_PRODUCT_DETAIL_TOTAL_SALES_ORG);
  }

  selectSalesOrg(salesOrganisation: string): void {
    this.selectedSalesOrg.set(salesOrganisation);
  }
}
